import { ReactNode } from "react";
import clsx from "clsx";

interface PageHeaderProps {
  /** Short, all-caps label rendered above the title. */
  eyebrow?: string;
  title: string;
  subtitle?: ReactNode;
  /** Right-aligned action(s): typically a primary button. */
  actions?: ReactNode;
  /** Optional decorative icon shown next to the title (e.g. a section glyph). */
  icon?: ReactNode;
  className?: string;
}

/**
 * Consistent page header used across the dashboard, campaigns, accounts, ... so
 * every page has the same visual rhythm: eyebrow + title + subtitle + actions.
 * Mobile-first: actions stack below the title on small viewports.
 */
export default function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
  icon,
  className,
}: PageHeaderProps): JSX.Element {
  return (
    <header className={clsx("flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between", className)}>
      <div className="min-w-0 space-y-1">
        {eyebrow && <p className="section-eyebrow">{eyebrow}</p>}
        <div className="flex items-center gap-3">
          {icon && (
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-500/15 text-brand-200 ring-1 ring-brand-400/30">
              {icon}
            </span>
          )}
          <h1 className="truncate text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h1>
        </div>
        {subtitle && (
          <p className="max-w-2xl text-sm text-slate-400 sm:text-[15px]">{subtitle}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">{actions}</div>}
    </header>
  );
}
